import { useNavigate } from 'react-router-dom';
import { useGuest } from '../context/GuestContext';
import { X, Zap, Mic, Swords, Users, Radio, MessageCircle } from 'lucide-react';

export default function LoginPromptModal() {
  const { showLoginPrompt, promptAction, dismissPrompt } = useGuest();
  const navigate = useNavigate();

  if (!showLoginPrompt) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center" data-testid="login-prompt-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismissPrompt} />

      {/* Modal */}
      <div className="relative w-full max-w-md mx-4 mb-0 sm:mb-0 bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
        {/* Close */}
        <button
          onClick={dismissPrompt}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-muted/50 text-muted-foreground transition-colors"
          data-testid="login-prompt-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6 sm:p-8 text-center space-y-5">
          {/* Icon */}
          <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-orange-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Zap className="w-7 h-7 text-white" />
          </div>

          {/* Title */}
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">
              Join to {promptAction || 'continue'}
            </h2>
            <p className="text-sm text-muted-foreground mt-1.5">
              Create a free account to unlock the full experience
            </p>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-2 gap-2.5 text-left text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Radio className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span>Go live</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mic className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span>Post content</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Swords className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span>Join battles</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="w-4 h-4 text-blue-400 flex-shrink-0" />
              <span>Build your audience</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MessageCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
              <span>Message creators</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Zap className="w-4 h-4 text-yellow-400 flex-shrink-0" />
              <span>Boost tracks</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3 pt-2">
            <button
              onClick={() => { dismissPrompt(); navigate('/signup'); }}
              className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-orange-500 to-purple-600 text-white font-semibold text-sm hover:opacity-90 transition-opacity shadow-lg"
              data-testid="login-prompt-signup"
            >
              Create Free Account
            </button>
            <button
              onClick={() => { dismissPrompt(); navigate('/login'); }}
              className="w-full py-3 px-4 rounded-xl bg-muted/50 border border-border text-foreground font-medium text-sm hover:bg-muted transition-colors"
              data-testid="login-prompt-login"
            >
              Already have an account? Log in
            </button>
          </div>

          {/* Dismiss */}
          <button
            onClick={dismissPrompt}
            className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          >
            Continue browsing
          </button>
        </div>
      </div>
    </div>
  );
}
